import { type ForwardRefRenderFunction, forwardRef, useMemo, useRef, useState } from 'react'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { FormHelperText, MenuItem } from '@mui/material'
import type { SelectInputProps } from '@mui/material/Select/SelectInput'
import { Icon } from 'components'
import { t } from 'i18next'
import { theme } from 'styles/theme/hav-theme'

import {
  FormControlStyled,
  LabelInfosStyled,
  LabelWrapper,
  SearchInputContainer,
  SelectInfoStyled,
} from './style'

export interface OptionProps {
  value: string | number
  title: string
}

export type WidthMenuProps = {
  startPosition: number | string
  endPosition: number | string
}

export interface SelectProps {
  value: string | number
  defaultValue?: string | number
  name: string
  label?: string
  height?: number
  options?: Array<OptionProps>
  onChange?: SelectInputProps<unknown>['onChange']
  error?: object | string | null
  required?: boolean
  focused?: boolean
  menuText?: string
  disabled?: boolean
  fullWidth?: boolean
  disableScrollLock?: boolean
  id?: string
  optionsMaxHeight?: string | number
  optionsMaxWidth?: WidthMenuProps
  labelAdornment?: React.ReactNode
  isMenu?: boolean
  hasSearch?: boolean
}

export const selectListStyle = {
  '& li': {
    ':not(:last-child)': {
      marginBottom: theme.spacing.xs,
    },
    border: `1px solid ${theme.colors.quartenary}`,
    borderRadius: theme.shapes.borderRadius.sm,
    fontSize: theme.typography.sizes.xs,
  },
  '& ul': {
    padding: 0,
  },
  gap: theme.spacing.sm,
  padding: theme.spacing.sm,
}

const SelectComponent: ForwardRefRenderFunction<HTMLInputElement, SelectProps> = (
  {
    value,
    defaultValue,
    name,
    label,
    height = 30,
    onChange,
    options = [],
    error = null,
    required = false,
    focused = false,
    menuText,
    disabled,
    disableScrollLock,
    id,
    optionsMaxHeight,
    optionsMaxWidth,
    labelAdornment,
    isMenu = false,
    fullWidth = true,
    hasSearch = false,
  },
  ref,
) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [searchValue, setSearchValue] = useState('')

  const filteredOptions = useMemo(() => {
    if (!hasSearch || searchValue === '') return options
    return options.filter((opt) =>
      opt.title.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()),
    )
  }, [searchValue, options, hasSearch])

  const handleMenuClose = () => {
    setTimeout(() => {
      setSearchValue('')
    }, 400)
  }

  const focusInput = () => {
    inputRef.current?.focus()
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Escape') {
      e.stopPropagation()
    }
  }

  return (
    <FormControlStyled fullWidth={fullWidth} variant="outlined" {...(error && { error: true })}>
      {label && (
        <LabelWrapper data-testid="label-select" disabled={disabled}>
          <LabelInfosStyled focused={focused} id="LabelInputForm" required={required}>
            {label}
          </LabelInfosStyled>
          {labelAdornment && <>{labelAdornment}</>}
        </LabelWrapper>
      )}
      <SelectInfoStyled
        data-testid="select"
        defaultValue={defaultValue}
        disabled={disabled}
        displayEmpty
        fullWidth={fullWidth}
        IconComponent={ExpandMoreIcon}
        inputProps={{ id: id }}
        inputRef={ref}
        MenuProps={{
          autoFocus: !hasSearch,
          disableScrollLock: disableScrollLock,
          PaperProps: {
            sx: {
              ...selectListStyle,
              left: `${optionsMaxWidth?.startPosition}px !important`,
              width: optionsMaxWidth?.endPosition,
            },
          },
          style: {
            maxHeight: optionsMaxHeight ? optionsMaxHeight : '100vh',
          },
          TransitionProps: {
            onEntered: () => {
              if (hasSearch) {
                inputRef.current?.focus()
              }
            },
          },
        }}
        name={name}
        onChange={onChange}
        onClose={handleMenuClose}
        style={{ height: height }}
        sx={{
          color: isMenu ? theme.colors.primary.main : theme.colors.neutral.black,
          fontFamily: isMenu ? theme.typography.family.medium : theme.typography.family.primary,
        }}
        value={value}
      >
        {hasSearch && (
          <MenuItem
            onClickCapture={(e) => {
              e.stopPropagation()
              e.preventDefault()
              focusInput()
            }}
            onKeyDown={handleSearchKeyDown}
            sx={{
              '&:focus-within': { backgroundColor: 'transparent' },
              '&:hover': { backgroundColor: 'transparent' },
              border: 'none !important',
              marginBottom: `${theme.spacing.xs} !important`,
              padding: 0,
            }}
          >
            <SearchInputContainer onClick={focusInput}>
              <input
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder={t('common.placeholders.typeToSearch')}
                ref={inputRef}
                value={searchValue}
              />
              <Icon icon="search" size="20" />
            </SearchInputContainer>
          </MenuItem>
        )}
        {!value && !options.some((opt) => opt.value === '') && (
          <MenuItem disabled value="">
            <em>{menuText?.length ? menuText : t('common.placeholders.select')}</em>
          </MenuItem>
        )}
        {filteredOptions.length > 0
          ? filteredOptions.map((option, key) => (
              <MenuItem key={key} value={option.value}>
                <span>{option.title}</span>
              </MenuItem>
            ))
          : hasSearch &&
            searchValue && (
              <MenuItem disabled sx={{ border: 'none !important' }}>
                <em>{t('common.messages.noOptionFound')}</em>
              </MenuItem>
            )}
      </SelectInfoStyled>
      {error && (
        <FormHelperText>{typeof error === 'string' ? error : JSON.stringify(error)}</FormHelperText>
      )}
    </FormControlStyled>
  )
}

export const Select = forwardRef(SelectComponent)
